<template>
    <slot name="header_close"/>

    <div class="wrapper">
        <h3 class="title">
            {{ $t("tiktok.settings.title") }}
            <div class="account-block">
                <img class="icon" :src="tiktokIcon"/>
                <span class="mt-1">{{ username }}</span>
            </div>
        </h3>

        <div class="content mt-4">
            <!-- VISIBILITY -->
            <div class="setting_block">
                <h4 class="block_title">
                    {{ $t("tiktok.settings.hide_account") }}
                </h4>
                <div class="action_container">
                    <Toggle v-if="!isLoading" v-model="isHide" @click="hideAccount"/>
                    <span v-else class="spinner"></span>
                </div>
            </div>

            <!-- API KEY -->
            <div class="setting_block">
                <h4 class="block_title">
                    {{ $t("dialog.settings-number.blocks.api_key") }}
                </h4>
                <div class="action_container">
                    <button class="inside_block text" @click="updateApiKey">
                        <RefreshApi class="icon"/>
                        {{ $t("common.update") }}
                    </button>
                </div>
            </div>

            <!-- TAGS -->
            <div class="setting_block wide_block">
                <div class="block_title">
                    <h4>{{ $t("tiktok.settings.tag_account") }}</h4>
                    <BaseTag :tag="tagList[0]" typeClass="table"/>
                </div>
                <div class="action_container" :class="{ multi_action: !!tagList.length }">
                    <button
                        v-if="!tagList.length"
                        class="inside_block text"
                        @click="actionTagMenu.add"
                    >
                        <i class="bi bi-plus-lg"></i>
                        {{ $t("common.add") }}
                    </button>
                    <template v-else>
                        <button
                            class="inside_block"
                            :title="$t('tags.edit_tag')"
                            @click="actionTagMenu.edit"
                        >
                            <i class="icon fa fa-pencil"></i>
                        </button>
                        <button
                            class="inside_block"
                            :title="$t('tags.delete_tag')"
                            @click="actionTagMenu.delete"
                        >
                            <i class="icon fa fa-trash"></i>
                        </button>
                    </template>
                </div>
            </div>

            <!-- WEBHOOKS -->
            <div
                class="setting_block wide_block"
                :style="!!webhook.length && { height: '60px' }"
            >
                <div class="block_title">
                    <h4>Webhook URL</h4>
                </div>
                <ul class="information" v-show="!!webhook.length">
                    <li class="info_item" :title="`Incoming/Outgoing WEBHOOK URL: ${webhook[0]}`">
                        <b class="item_title"> In/Out </b>
                        <i class="text_overflow">{{ webhook[0] }}</i>
                    </li>
                    <li class="info_item" :title="`Status WEBHOOK URL: ${webhook[1]}`">
                        <b class="item_title">Status </b>
                        <i class="text_overflow">{{ webhook[1] }}</i>
                    </li>
                </ul>
                <div class="action_container multi_action">
                    <button
                        v-if="!webhook.length"
                        class="inside_block text"
                        @click="actionWebhookMenu.add"
                    >
                        <i class="bi bi-plus-lg"></i>
                        {{ $t("common.add") }}
                    </button>
                    <template v-else>
                        <button
                            class="inside_block"
                            :title="$t('common.edit')"
                            @click="actionWebhookMenu.edit"
                        >
                            <i class="icon fa fa-pencil"></i>
                        </button>
                        <button
                            class="inside_block"
                            :title="$t('common.delete')"
                            @click="actionWebhookMenu.delete"
                        >
                            <i class="icon fa fa-trash"></i>
                        </button>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
// core
import {computed, ref, watch, getCurrentInstance} from "vue";
import {useStore} from "vuex";

// components
import Toggle from "@vueform/toggle";
import BaseTag from "@/components/ui/BaseTag.vue";

// icons
import RefreshApi from "@/components/Telephony/icons/RefreshApi.vue";

import tiktokIcon from "@/assets/icons/tiktok.svg";

const store = useStore();
const {proxy} = getCurrentInstance();

const isHide = ref(null);
const isLoading = ref(false);

const userId = computed(() => store.state.dialogParams?.info?.user_id);
const username = computed(() => store.state.dialogParams?.info?.username);
const visibility = computed(() => store.state.dialogParams?.info?.visibility);
const tagList = computed(() => store.state.dialogParams?.info?.tags || []);

const webhook = computed(() => {
    const arr = [];
    (store.state.dialogParams?.info?.webhooks ?? []).forEach((el) => {
        el.type === "inbox" ? (arr[0] = el.url) : (arr[1] = el.url);
    });
    return arr;
});

watch(
    visibility,
    () => {
        isHide.value = !visibility.value;
    },
    {immediate: true}
);

const hideAccount = async () => {
    isLoading.value = true;

    try {
        await store.dispatch("tiktok/updateVisibility", {
            userId: userId.value,
            visibility: !isHide.value,
            moshaToast: proxy.$moshaToast,
        });

        store.commit("TOGGLE_HIDE_NUMBER", !isHide.value);

        await Promise.all([
            store.dispatch("tiktok/loadAccounts", {visibility: true}),
            store.dispatch("tiktok/loadAccounts", {visibility: false}),
        ]);
    } finally {
        isLoading.value = false;
    }
};

const openNext = (name) => {
    store.commit("setNextDialog", "TiktokAccountSettingsDialog");
    store.commit("setDialog", name);
};

const updateApiKey = () => openNext("TiktokApiKeyRefreshDialog");

const actionTagMenu = {
    add: () => openNext("TiktokTagAddEditDialog"),
    edit: () => openNext("TiktokTagAddEditDialog"),
    delete: () => openNext("TiktokTagDeleteDialog"),
};

const actionWebhookMenu = {
    add: () => {
        store.commit("setTypeOfDialogAction", "add");
        openNext("TiktokWebhookAddEditDialog");
    },
    edit: () => {
        store.commit("setTypeOfDialogAction", "edit");
        openNext("TiktokWebhookAddEditDialog");
    },
    delete: () => {
        store.commit("setTypeOfDialogAction", "delete");
        openNext("TiktokWebhookDeleteDialog");
    },
};
</script>

<style lang="scss">
@use "@/styles/toggle.scss";
</style>

<style lang="scss" scoped>
.wrapper {
  padding: 20px 28px 42px;
  max-width: 750px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 7px;
  background-color: white;
  text-align: center;
}

.title {
  font-size: 20px;
  text-align: center;
  font-family: Gilroy;
  font-weight: 500;
  text-transform: uppercase;
  color: var(--text-color);
}

.icon {
  width: 20px;
  height: 20px;
  font-size: 18px;
}

.account-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-color);
}

.content {
  display: flex;
  gap: 10px;
  justify-content: center;
  // justify-content: space-between;
  flex-wrap: wrap;
}

.action_container {
  display: flex;
  justify-content: center;
}
// the spinner in place of the toggle, as in dialog-form.scss
.action_container .spinner {
  width: 1.4em;
  height: 1.4em;
  margin: 0;
}
.multi_action {
  width: fit-content;
  gap: 10px;
}

.setting_block {
  height: 50px;
  min-width: 240px;
  width: calc(50% - 5px);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 7px;
  margin-bottom: 20px;
  border: 1px solid var(--general-color);
  background-color: var(--block-color);
  transition: all 0.7s ease;
  &:hover {
    background-color: var(--block-second-color);
    // transform: translate(1px, 1px);
  }
}

.wide_block {
  width: 100%;
  justify-content: space-between;
  .block_title {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: none;
    gap: 10px;
    width: fit-content;
  }
}

.setting_block:not(.wide_block) .block_title {
  white-space: nowrap;
}

.block_title {
  flex: 1;
  text-align: start;
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  text-transform: uppercase;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.6);
}

.information {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 14px;
  line-height: 19px;
  font-size: 14px;
  overflow: hidden;
  //   border-left: 2px dashed var(--border-light-color);
  //   border-right: 2px dashed var(--border-light-color);
  //   margin: 0 -3px 0 -5px;
  //   padding-left: 5px;
}

.info_item {
  cursor: pointer;
  display: flex;
  gap: 6px;
  width: 100%;
  min-width: 0;
  color: var(--text-third-color);
  align-items: center;
}

// the url scrolls behind a fade, as the password in the sip cards
.text_overflow {
  flex: 1 1 0;
  min-width: 0;
  overflow-x: auto;
  white-space: nowrap;

  // html sets Figtree, which the project does not ship
  font-family: "Gilroy", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.05rem;
  color: var(--text-third-color);

  // the scrollbar eats 5px inside the row and lifts the url off the label
  // baseline; wheel and trackpad scrolling still work
  scrollbar-width: none;
  -ms-overflow-style: none;

  -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent 100%);
  mask-image: linear-gradient(to right, #000 calc(100% - 24px), transparent 100%);

  &::-webkit-scrollbar {
    display: none;
  }
}

// the tag reads as a value, its brackets as quiet as the webhook labels
.wide_block :deep(.badge.table) {
  margin-top: 0;
  color: var(--border-primary-color);

  .tags {
    gap: 4px;
  }

  .tag {
    max-width: 220px;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.05rem;
    color: var(--text-third-color);
  }
}

.inside_block {
  position: relative;
  min-width: 35px;
  width: 35px;
  height: 35px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 9px;
  border: 1px solid var(--general-color);
  background-color: #ffffff;

  transition: 0.25s;
  &.text {
    width: 140px;
    min-width: 140px;
    padding: 0 16px;
    gap: 10px;
    color: var(--general-color);
    font-family: "Gilroy", sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 17px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    white-space: nowrap;

    i {
      font-size: 16px;
    }

    .icon {
      flex: 0 0 auto;
      width: 22px;
      height: 22px;
    }
  }
  .icon {
    fill: var(--general-color);
    color: var(--general-color);
    transition: 0.25s;
  }

  &:hover {
    cursor: pointer;
    background-color: var(--general-color);
    transition: 0.25s;
    &.text {
      color: white;
      transition: 0.25s;
    }
    .icon {
      fill: white;
      color: white;
      transition: 0.25s;
    }
  }
}

.item_title {
  flex: 0 0 auto;
  display: flex;
  font-size: 12px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--border-primary-color);
  &:after {
    content: ":";
  }
}

@media (max-width: 570px) {
  .setting_block {
    width: 100%;
  }
}

@media (max-width: 420px) {
  .information {
    line-height: 22px;
  }
  .info_item {
  }
  .text_overflow {
    display: none;
  }
  .item_title {
    margin: auto;
    color: var(--general-color);
    transition: 0.5s;
    &:after {
      content: "";
    }
    &:hover {
      color: var(--text-light-color);
      transition: 0.5s;
    }
  }
}
</style>
